import {
  InfoAlert,
  Heading_1 as Heading1,
  PrimaryButton,
  SecondaryButton,
  WarningAlert,
  ErrorAlert,
  useThemeContext,
} from '@huggg/react-components';
import { Pin } from 'components/forms';
import dictionary from 'config/dictionary';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { genericError } from 'utilities/errorHandling';
import { getKnownProviderErrors } from 'utilities/prepaidProviderErrors';
import BackButton from '../BackButton/BackButton';
import SvgIcon from '../SvgIcon/SvgIcon';
import { PrepaidFormDetails } from './PrepaidFormDetails';
import styles from './prepaidCard.module.scss';

export const PrepaidFormConfirmation = (props) => {
  const { operator } = useThemeContext();
  const {
    formData,
    onSubmit,
    onBack,
    onResendOtp,
    otpDelay,
    voucherError,
    shortcode,
    declineMessage,
    unknownApiErrorMessage,
  } = props;
  const [showOtpResend, setShowOtpResend] = useState(false);

  const toggleOtpResend = () => {
    setShowOtpResend(!showOtpResend);
  };

  const knownProviderErrors = useMemo(
    () => getKnownProviderErrors(shortcode, declineMessage, operator),
    [shortcode, declineMessage, operator],
  );
  const providerErrorMessage = useMemo(
    () => voucherError?.metadata?.providerError?.message,
    [voucherError],
  );
  const shouldDeclineRequest = useMemo(
    () =>
      providerErrorMessage &&
      knownProviderErrors[providerErrorMessage]?.declineCardRequest,
    [providerErrorMessage, knownProviderErrors],
  );

  const getError = () => {
    if (shouldDeclineRequest) {
      return (
        <WarningAlert
          title="Card request declined"
          message={knownProviderErrors[providerErrorMessage]?.displayMessage}
        />
      );
    } else {
      return (
        <ErrorAlert
          message={
            knownProviderErrors[providerErrorMessage]?.displayMessage ??
            (operator.exposeApiErrorMessages ? providerErrorMessage : null) ??
            unknownApiErrorMessage ??
            genericError(operator)
          }
        />
      );
    }
  };

  return (
    <div>
      {!shouldDeclineRequest && (
        <Heading1 className={styles.heavyMargin}>
          Confirm your personal details
        </Heading1>
      )}
      <div className={styles.confirmationBlock}>
        <PrepaidFormDetails
          formData={formData}
          emailAction={
            !shouldDeclineRequest && (
              <button className={styles.buttonAsLink} onClick={onBack}>
                Change
              </button>
            )
          }
        />
      </div>
      {voucherError && <div className={styles.detailsAlert}>{getError()}</div>}
      <div>
        {!shouldDeclineRequest && (
          <div className={styles.otpBlock}>
            <InfoAlert message={dictionary.prepaidCards.copy.enterOtp} />

            <button className={styles.buttonAsLink} onClick={toggleOtpResend}>
              {dictionary.prepaidCards.labels.didntReceiveOtp}
              <SvgIcon
                name={showOtpResend ? 'chevron-up' : 'chevron-down'}
                className={styles.chevronIcon}
              />
            </button>

            {!!showOtpResend && (
              <>
                <SecondaryButton
                  disabled={otpDelay > 0}
                  style={{ border: '1px solid black' }}
                  arrow="none"
                  label={`RESEND ONE TIME PASSCODE ${
                    otpDelay > 0 ? `IN ${otpDelay}s` : ''
                  }`}
                  onClick={onResendOtp}
                />
                <div className={styles.otpReminder}>
                  Please check that you have entered the correct email address
                  and don’t forget to check your spam/junk folders too.
                </div>
              </>
            )}
            <Pin
              className={styles.pinInput}
              pinLength={6}
              requiredValidationError="Please enter a valid, 6-digit One Time Passcode"
              onSubmit={onSubmit}
              button={<PrimaryButton label="CLAIM MY CARD" />}
            />
          </div>
        )}
        {!shouldDeclineRequest && (
          <div className={styles.backButton}>
            <BackButton onClick={onBack} />
          </div>
        )}
      </div>
    </div>
  );
};

PrepaidFormConfirmation.propTypes = {
  formData: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onResendOtp: PropTypes.func.isRequired,
  voucherError: PropTypes.object,
  shortcode: PropTypes.string,
  declineMessage: PropTypes.string,
  unknownApiErrorMessage: PropTypes.string,
};
